import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Layers,
    Zap,
    Cpu,
    Globe,
    Minimize2,
    Maximize2
} from 'lucide-react';

const VisionPage = () => {
    const [activeSection, setActiveSection] = useState(null);
    const [backgroundPosition, setBackgroundPosition] = useState({ x: 50, y: 50 });
    const containerRef = useRef(null);

    // Dynamic background movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                setBackgroundPosition({ x, y });
            }
        };

        const currentContainer = containerRef.current;
        if (currentContainer) {
            currentContainer.addEventListener('mousemove', handleMouseMove);
            return () => {
                currentContainer.removeEventListener('mousemove', handleMouseMove);
            };
        }
    }, []);

    const sections = [
        {
            icon: <Zap className="w-8 h-8" />,
            title: "Immortal Evolution",
            description: "Transcending biological limits through technological fusion.",
            details: "We redefine existence by merging human consciousness with advanced technological systems, creating pathways to continuous adaptation and growth."
        },
        {
            icon: <Cpu className="w-8 h-8" />,
            title: "Autonomous Transformation",
            description: "Systems that evolve beyond human intervention.",
            details: "Designing intelligent ecosystems that learn, adapt, and transform independently, creating a legacy that outlasts individual lifespans."
        },
        {
            icon: <Layers className="w-8 h-8" />,
            title: "Art of Preservation",
            description: "Capturing consciousness through immersive technologies.",
            details: "Transforming art and memory into living, evolving digital entities that preserve and expand human experience beyond physical limitations."
        },
        {
            icon: <Globe className="w-8 h-8" />,
            title: "Cosmic Exploration",
            description: "Extending human potential beyond terrestrial boundaries.",
            details: "Creating autonomous systems that enable humanity to explore, inhabit, and transform environments beyond Earth, turning space into a canvas of infinite possibilities."
        }
    ];

    return (
        <div
            ref={containerRef}
            className="relative min-h-screen w-full bg-black text-white font-mono overflow-hidden"
            style={{
                backgroundImage: `radial-gradient(circle at ${backgroundPosition.x}% ${backgroundPosition.y}%, rgba(30,30,30,0.9), rgba(0,0,0,1))`,
                backgroundSize: 'cover',
                transition: 'background-position 0.5s ease'
            }}
        >
            <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-purple-900 via-transparent to-blue-900"></div>

            <div className="relative z-10 max-w-7xl mx-auto px-8 py-16">
                {/* Header */}
                <div className="mb-24 flex justify-between items-start">
                    <div>
                        <div className="mb-4">
                            <div className="relative overflow-hidden rounded-md" style={{ maxWidth: '300px' }}>
                                <div className="relative z-10">
                                    <h1 className="text-4xl tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                                        Exit Death
                                    </h1>
                                </div>
                                <div className="absolute inset-0 pointer-events-none" style={{ 
                                    background: 'radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.8) 100%)',
                                    zIndex: 20
                                }}></div>
                            </div>
                        </div>
                        <p className="text-xl max-w-xl opacity-70">
                            A manifesto of transformation, breaking the boundaries of mortality through technology, art,
                            and boundless imagination.
                        </p>
                    </div>
                    <Link
                        to="/"
                        className="border border-white/20 px-4 py-2 rounded-full hover:bg-white/10 transition-all"
                    >
                        Return
                    </Link>
                </div>

                {/* Grid of Sections */}
                <div className="grid grid-cols-2 gap-8">
                    {sections.map((section, index) => (
                        <div
                            key={index}
                            className={`
                                border border-white/10 rounded-xl p-6 
                                transform transition-all duration-300
                                ${activeSection === index
                                ? 'scale-105 bg-white/5 border-white/30'
                                : 'hover:bg-white/5 hover:border-white/20'}
                                relative overflow-hidden
                            `}
                            onClick={() => setActiveSection(activeSection === index ? null : index)}
                        >
                            <div className="flex items-center space-x-4 mb-4 relative z-10">
                                <div className="bg-white/10 p-3 rounded-full">
                                    {section.icon}
                                </div>
                                <h2 className="text-2xl font-light">{section.title}</h2>
                            </div>
                            <p className="opacity-70 mb-4 relative z-10">{section.description}</p>

                            {activeSection === index && (
                                <div className="mt-6 space-y-4 relative z-10 animate-fade-in">
                                    <p className="text-sm leading-relaxed">{section.details}</p>
                                    <div className="flex space-x-2">
                                        {activeSection === index ? (
                                            <Minimize2 className="w-5 h-5 opacity-50"/>
                                        ) : (
                                            <Maximize2 className="w-5 h-5 opacity-50"/>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Subtle animated background effect */}
                            <div
                                className="absolute inset-0 opacity-20 blur-2xl animate-pulse"
                                style={{
                                    background: 'radial-gradient(circle at center, rgba(79,70,229,0.3), transparent 70%)',
                                }}
                            ></div>
                        </div>
                    ))}
                </div>
                <footer className="text-center text-gray-500 mt-12">
                    © Exit Death. Redefining the boundaries of existence.
                </footer>
            </div>
        </div>
    );
};

export default VisionPage;